<template>
  <div class="min-h-screen bg-[#F8FAFC] p-4 md:p-6 lg:p-8 font-sans text-slate-900 space-y-6 selection:bg-blue-100">
    
    <!-- EN-TÊTE PRINCIPAL -->
    <header class="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
      <div class="space-y-1">
        <div class="flex items-center gap-2 text-xs font-semibold text-slate-400">
          <span>Medialink Solutions</span>
          <span>/</span>
          <span class="text-blue-600">Portefeuille Officines & Clients</span>
        </div>
        <h1 class="text-2xl md:text-3xl font-black text-slate-900 tracking-tight">
          Supervision des Agences & Licences
        </h1>
      </div>
      
      <button 
        @click="showAddModal = true"
        class="flex items-center gap-2 bg-blue-600 text-white px-5 py-2.5 rounded-xl font-bold text-xs hover:bg-blue-700 shadow-sm transition-all"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2.5" d="M12 4v16m8-8H4" /></svg>
        Nouvelle Agence
      </button>
    </header>

    <!-- BARRE DE RECHERCHE ET FILTRES -->
    <div class="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm flex flex-wrap gap-4 items-center justify-between">
      <div class="flex-1 min-w-[280px] relative">
        <span class="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
        </span>
        <input 
          type="text" 
          v-model="searchQuery" 
          placeholder="Rechercher une pharmacie par nom ou email..." 
          class="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-blue-500 text-xs font-medium text-slate-700"
        >
      </div>
      <button 
        v-if="searchQuery" 
        @click="searchQuery = ''" 
        class="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl text-xs font-bold transition-all"
      >
        Réinitialiser
      </button>
    </div>

    <!-- TABLEAU DU PORTEFEUILLE CLIENTS -->
    <div class="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
      
      <!-- État de chargement -->
      <div v-if="loading" class="p-8 space-y-4">
        <div v-for="n in 5" :key="n" class="flex items-center justify-between animate-pulse">
           <div class="flex gap-3 w-1/3 items-center">
             <div class="h-9 w-9 bg-slate-100 rounded-xl"></div>
             <div class="space-y-2 flex-1">
               <div class="h-3 bg-slate-100 rounded w-3/4"></div>
               <div class="h-2 bg-slate-100 rounded w-1/2"></div>
             </div>
           </div>
           <div class="h-3 bg-slate-100 rounded w-20"></div>
           <div class="h-8 bg-slate-100 rounded w-28"></div>
        </div>
      </div>

      <template v-else-if="clients.length === 0">
        <div class="p-16 text-center flex flex-col items-center justify-center">
          <div class="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mb-3 border border-slate-200">
            <svg class="w-8 h-8 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="1.5" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
          </div>
          <h3 class="text-base font-bold text-slate-800 mb-1">Aucune agence trouvée</h3>
          <p class="text-slate-400 text-xs">Commencez par déployer votre première infrastructure client.</p>
        </div>
      </template>

      <template v-else>
        <div class="overflow-x-auto">
          <table class="w-full border-collapse">
            <thead>
              <tr class="bg-slate-50/75 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100">
                <th class="px-6 py-4">Pharmacie / Client</th>
                <th class="px-6 py-4 text-center">Bornes Kiosk</th>
                <th class="px-6 py-4 text-center">Écrans TV</th>
                <th class="px-6 py-4 text-center">Statut Système</th>
                <th class="px-6 py-4 text-right">Configuration</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-100 text-xs">
              <tr v-for="client in paginatedClients" :key="client.id" class="hover:bg-slate-50/60 transition-colors group">
                <td class="px-6 py-4">
                  <div class="flex items-center gap-3">
                    <div class="w-9 h-9 bg-blue-50 text-blue-600 border border-blue-100 rounded-xl flex items-center justify-center font-bold text-xs shrink-0 shadow-sm">
                      {{ client.nom.charAt(0) }}
                    </div>
                    <div>
                      <p class="font-bold text-slate-900 group-hover:text-blue-600 transition-colors">{{ client.nom }}</p>
                      <p class="text-[10px] text-slate-400 font-mono">{{ client.siret }}</p>
                    </div>
                  </div>
                </td>
                <td class="px-6 py-4">
                  <div class="flex flex-col items-center gap-1.5">
                    <span class="text-xs font-bold text-slate-700 tabular-nums">{{ client.bornesActives }} / {{ client.bornesTotal }}</span>
                    <div class="w-20 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div class="h-full bg-blue-600 rounded-full transition-all duration-300" :style="{ width: (client.bornesTotal > 0 ? (client.bornesActives / client.bornesTotal * 100) : 0) + '%' }"></div>
                    </div>
                  </div>
                </td>
                <td class="px-6 py-4 text-center">
                  <span class="px-2.5 py-1 bg-purple-50 text-purple-700 rounded-lg text-[10px] font-bold uppercase tracking-wider border border-purple-200">
                    {{ client.ecrans }} Devices
                  </span>
                </td>
                <td class="px-6 py-4 text-center">
                  <span :class="statusClasses(client.status)" class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border">
                    <span class="w-1.5 h-1.5 rounded-full bg-current"></span>
                    {{ client.statusLabel }}
                  </span>
                </td>
                <td class="px-6 py-4 text-right">
                  <button @click="openConfig(client)" class="p-2 bg-white border border-slate-200 text-slate-500 hover:text-blue-600 hover:border-blue-200 hover:bg-blue-50 rounded-xl transition-all shadow-sm" title="Paramétrer l'agence">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37a1.724 1.724 0 002.572-1.065z" /><path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <TablePagination
          v-if="filteredClients.length > 0"
          :current-page="currentPage"
          @update:current-page="currentPage = $event"
          :total-items="filteredClients.length"
          :items-per-page="itemsPerPage"
          @update:items-per-page="itemsPerPage = $event; currentPage = 1"
          item-label="clients"
          class="px-6 py-4 border-t border-slate-100"
        />
      </template>
    </div>

    <!-- MODALE NOUVELLE AGENCE -->
    <Transition name="fade">
      <div v-if="showAddModal" class="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div class="bg-white w-full max-w-lg rounded-2xl p-6 shadow-xl border border-slate-200">
          <div class="flex justify-between items-center mb-6">
             <h3 class="text-lg font-bold text-slate-900">Nouvelle Agence</h3>
             <button @click="showAddModal = false" class="text-slate-400 hover:text-slate-600">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
          
          <div class="space-y-4 text-xs">
            <div>
               <label class="block font-bold text-slate-500 mb-1">Raison Sociale</label>
               <input v-model="newClient.nom" type="text" placeholder="Ex: Pharmacie de la Gare" class="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl font-medium text-slate-800 focus:outline-none focus:border-blue-500">
            </div>
            <div>
               <label class="block font-bold text-slate-500 mb-1">Email de contact</label>
               <input v-model="newClient.email" type="email" placeholder="contact@pharmacie.com" class="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl font-medium text-slate-800 focus:outline-none focus:border-blue-500">
            </div>
            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="block font-bold text-slate-500 mb-1">Quota Bornes</label>
                <input v-model.number="newClient.bornesTotal" type="number" class="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl font-medium text-slate-800 focus:outline-none focus:border-blue-500">
              </div>
              <div>
                <label class="block font-bold text-slate-500 mb-1">Quota Écrans</label>
                <input v-model.number="newClient.ecrans" type="number" class="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl font-medium text-slate-800 focus:outline-none focus:border-blue-500">
              </div>
            </div>
          </div>

          <div class="mt-6 flex justify-end gap-2 pt-4 border-t border-slate-100 text-xs">
            <button @click="showAddModal = false" class="px-4 py-2 rounded-xl border border-slate-200 font-bold hover:bg-slate-50">Annuler</button>
            <button @click="saveNewClient" class="px-4 py-2 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 shadow-sm">Créer le compte</button>
          </div>
        </div>
      </div>
    </Transition>

    <!-- PANNEAU LATÉRAL DE CONFIGURATION MASTER -->
    <Transition name="slide-right">
      <div v-if="selectedClient" class="fixed inset-0 z-50 overflow-hidden">
        <div class="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]" @click="selectedClient = null"></div>
        <div class="absolute right-0 top-0 bottom-0 w-full max-w-xl bg-white shadow-xl p-6 lg:p-8 overflow-y-auto border-l border-slate-200">
          
          <div class="flex justify-between items-start mb-6 pb-4 border-b border-slate-100">
            <div>
              <span class="text-[10px] font-bold uppercase text-blue-600 bg-blue-50 border border-blue-200 px-2.5 py-1 rounded-full">Configuration Master</span>
              <h2 class="text-xl font-bold text-slate-900 mt-1.5">{{ selectedClient.nom }}</h2>
              <p class="text-xs text-slate-400 font-mono">{{ selectedClient.siret }}</p>
            </div>
            <button @click="selectedClient = null" class="p-2 bg-slate-50 rounded-xl hover:bg-slate-100 text-slate-400">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>

          <div class="space-y-6 text-xs">
            
            <!-- Identité & Quotas -->
            <section class="bg-slate-50 p-5 rounded-2xl border border-slate-200 space-y-4">
              <h3 class="text-[10px] font-bold uppercase tracking-wider text-slate-400">Identité & Quotas</h3>
              
              <div class="space-y-3">
                <div>
                  <label class="block font-bold text-slate-500 mb-1">Raison Sociale</label>
                  <input v-model="selectedClient.nom" type="text" class="w-full bg-white border border-slate-200 rounded-xl py-2.5 px-3.5 font-medium text-slate-800 focus:outline-none focus:border-blue-500">
                </div>
                <div>
                  <label class="block font-bold text-slate-500 mb-1">Email (Login)</label>
                  <input v-model="selectedClient.siret" type="text" class="w-full bg-white border border-slate-200 rounded-xl py-2.5 px-3.5 font-medium text-slate-800 focus:outline-none focus:border-blue-500">
                </div>
              </div>

              <div class="grid grid-cols-2 gap-3">
                <div>
                  <label class="block font-bold text-slate-500 mb-1">Bornes Max</label>
                  <input v-model.number="selectedClient.bornesTotal" type="number" class="w-full bg-white border border-slate-200 rounded-xl py-2.5 px-3.5 font-medium text-slate-800 focus:outline-none focus:border-blue-500">
                </div>
                <div>
                  <label class="block font-bold text-slate-500 mb-1">Écrans Max</label>
                  <input v-model.number="selectedClient.ecrans" type="number" class="w-full bg-white border border-slate-200 rounded-xl py-2.5 px-3.5 font-medium text-slate-800 focus:outline-none focus:border-blue-500">
                </div>
              </div>

              <!-- Toggles État et Options -->
              <div class="space-y-2.5 pt-2">
                <div class="flex items-center justify-between p-3.5 bg-white rounded-xl border border-slate-200">
                    <div>
                        <span class="font-bold text-slate-800">Accès Système</span>
                        <p class="text-[10px] text-slate-400">Autoriser la connexion</p>
                    </div>
                    <button @click="toggleStatus(selectedClient)"
                        :class="selectedClient.status === 'active' ? 'bg-emerald-500' : 'bg-slate-300'"
                        class="w-12 h-6 relative rounded-full transition-colors focus:outline-none">
                        <span :class="selectedClient.status === 'active' ? 'translate-x-6' : 'translate-x-1'" class="absolute top-0.5 left-0 w-5 h-5 bg-white rounded-full shadow-sm transition-transform"></span>
                    </button>
                </div>

                <div class="flex items-center justify-between p-3.5 bg-white rounded-xl border border-slate-200">
                    <div>
                        <span class="font-bold text-slate-800">Écran de Langues</span>
                        <p class="text-[10px] text-slate-400">Actif sur les bornes APK</p>
                    </div>
                    <button @click="toggleLanguageScreen(selectedClient)"
                        :class="selectedClient.showLanguageScreen !== false ? 'bg-blue-600' : 'bg-slate-300'"
                        class="w-12 h-6 relative rounded-full transition-colors focus:outline-none">
                        <span :class="selectedClient.showLanguageScreen !== false ? 'translate-x-6' : 'translate-x-1'" class="absolute top-0.5 left-0 w-5 h-5 bg-white rounded-full shadow-sm transition-transform"></span>
                    </button>
                </div>

                <div class="flex items-center justify-between p-3.5 bg-white rounded-xl border border-slate-200">
                    <div>
                        <span class="font-bold text-slate-800">Écran Pocket Care</span>
                        <p class="text-[10px] text-slate-400">Module complémentaire</p>
                    </div>
                    <button @click="toggleFamilyPackScreen(selectedClient)"
                        :class="selectedClient.showFamilyPackScreen === true ? 'bg-blue-600' : 'bg-slate-300'"
                        class="w-12 h-6 relative rounded-full transition-colors focus:outline-none">
                        <span :class="selectedClient.showFamilyPackScreen === true ? 'translate-x-6' : 'translate-x-1'" class="absolute top-0.5 left-0 w-5 h-5 bg-white rounded-full shadow-sm transition-transform"></span>
                    </button>
                </div>
              </div>
            </section>

            <!-- Écrans TV Connectés -->
            <section class="space-y-4">
              <h4 class="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Écrans TV Connectés</h4>
              <div v-if="selectedClient.devices.filter(d => d.type === 'SCREEN').length === 0" class="p-3 border border-dashed border-slate-200 rounded-xl text-center text-slate-400">Aucun écran configuré</div>
              <div
                v-for="screen in selectedClient.devices.filter(d => d.type === 'SCREEN')"
                :key="screen._id"
                class="p-4 bg-white border border-slate-200 rounded-xl space-y-3 shadow-sm"
              >
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-3">
                    <div class="w-8 h-8 bg-blue-50 text-blue-600 rounded-lg flex items-center justify-center">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                    </div>
                    <div>
                      <div v-if="editingDeviceId === screen._id" class="flex items-center gap-2">
                        <input v-model="editingDeviceName" type="text" @keyup.enter="saveDeviceName(screen)" class="text-xs font-bold bg-slate-50 border border-slate-300 rounded px-2 py-1" />
                        <button @click="saveDeviceName(screen)" class="text-emerald-600 font-bold">OK</button>
                      </div>
                      <div v-else class="flex items-center gap-2">
                        <span class="font-bold text-slate-900">{{ screen.name }}</span>
                        <button @click="startEditDeviceName(screen)" class="text-slate-400 hover:text-blue-600 text-[10px]">Renommer</button>
                      </div>
                    </div>
                  </div>
                  <button @click="copyLink(screen.connectionUrl)" class="bg-slate-100 hover:bg-slate-200 text-slate-700 text-[10px] font-bold px-3 py-1.5 rounded-lg">Copier URL</button>
                </div>
              </div>
            </section>
            
            <!-- Bornes Interactives -->
            <section class="space-y-4">
              <h4 class="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Bornes Interactives</h4>
              <div v-if="selectedClient.devices.filter(d => d.type === 'KIOSK').length === 0" class="p-3 border border-dashed border-slate-200 rounded-xl text-center text-slate-400">Aucune borne configurée</div>
              <div
                v-for="kiosk in selectedClient.devices.filter(d => d.type === 'KIOSK')"
                :key="kiosk._id"
                class="p-4 bg-white border border-slate-200 rounded-xl space-y-3 shadow-sm"
              >
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-3">
                    <div class="w-8 h-8 bg-blue-50 text-blue-600 rounded-lg flex items-center justify-center">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                    </div>
                    <div>
                      <span class="font-bold text-slate-900">{{ kiosk.name }}</span>
                    </div>
                  </div>
                  <button @click="showKioskKey(kiosk)" class="bg-slate-100 hover:bg-slate-200 text-slate-700 text-[10px] font-bold px-3 py-1.5 rounded-lg">
                    {{ openedKioskId === kiosk._id ? "Masquer Token" : "Voir Token" }}
                  </button>
                </div>
                <div v-if="openedKioskId === kiosk._id" class="p-3 bg-slate-900 text-white rounded-lg font-mono text-[10px] break-all">
                  {{ kiosk.connectionToken }}
                </div>
              </div>
            </section>

            <!-- Files d'attente -->
            <section class="space-y-3">
               <h4 class="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Files d’attente</h4>
               <div v-if="selectedClient.queues.length === 0" class="p-3 border border-dashed border-slate-200 rounded-xl text-center text-slate-400">Aucune file configurée</div>
               <div v-for="queue in selectedClient.queues" :key="queue._id" class="flex items-center justify-between p-3 bg-white rounded-xl border border-slate-200 shadow-sm">
                <div class="flex items-center gap-2.5">
                  <span class="w-6 h-6 flex items-center justify-center rounded bg-slate-100 text-slate-700 font-bold text-[10px]">{{ queue.prefix }}</span>
                  <span class="font-bold text-slate-800">{{ queue.name }}</span>
                </div>
                <span :class="queue.active ? 'text-emerald-600 bg-emerald-50' : 'text-slate-400 bg-slate-50'" class="px-2 py-0.5 rounded text-[10px] font-bold uppercase">
                  {{ queue.active ? 'Active' : 'Inactive' }}
                </span>
              </div>
            </section>

            <!-- Personnalisation & Branding -->
            <section class="space-y-4">
              <h4 class="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-2">
                <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-width="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                </svg>
                Personnalisation & Branding
              </h4>

              <!-- Logo -->
              <div class="bg-slate-50 p-5 rounded-2xl border border-slate-200 space-y-3">
                <label class="block font-bold text-slate-500 mb-2">Logo Sidebar</label>

                <div v-if="branding?.logo?.url" class="relative group">
                  <img :src="branding.logo.url" alt="Logo agence" class="h-16 w-auto object-contain border border-slate-200 rounded-xl bg-white p-2" />
                  <button @click="removeLogo" class="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>

                <MediaPicker
                  @media-selected="onLogoSelected"
                  button-text="Sélectionner un logo"
                  :max-files="1"
                />
              </div>

              <!-- Couleurs Sidebar -->
              <div class="bg-slate-50 p-5 rounded-2xl border border-slate-200 space-y-4">
                <label class="block font-bold text-slate-500 mb-2">Couleurs de la Sidebar</label>

                <div class="grid grid-cols-2 gap-3">
                  <div>
                    <label class="text-[10px] font-bold text-slate-600 mb-1 block">Fond</label>
                    <div class="flex items-center gap-2">
                      <input type="color" v-model="brandingColors.background" class="w-10 h-10 rounded border border-slate-300 cursor-pointer" />
                      <input type="text" v-model="brandingColors.background" class="flex-1 px-2 py-1.5 bg-white border border-slate-200 rounded text-xs font-mono" />
                    </div>
                  </div>

                  <div>
                    <label class="text-[10px] font-bold text-slate-600 mb-1 block">Texte</label>
                    <div class="flex items-center gap-2">
                      <input type="color" v-model="brandingColors.text" class="w-10 h-10 rounded border border-slate-300 cursor-pointer" />
                      <input type="text" v-model="brandingColors.text" class="flex-1 px-2 py-1.5 bg-white border border-slate-200 rounded text-xs font-mono" />
                    </div>
                  </div>

                  <div>
                    <label class="text-[10px] font-bold text-slate-600 mb-1 block">Texte Secondaire</label>
                    <div class="flex items-center gap-2">
                      <input type="color" v-model="brandingColors.textMuted" class="w-10 h-10 rounded border border-slate-300 cursor-pointer" />
                      <input type="text" v-model="brandingColors.textMuted" class="flex-1 px-2 py-1.5 bg-white border border-slate-200 rounded text-xs font-mono" />
                    </div>
                  </div>

                  <div>
                    <label class="text-[10px] font-bold text-slate-600 mb-1 block">Bouton Actif</label>
                    <div class="flex items-center gap-2">
                      <input type="color" v-model="brandingColors.activeButton" class="w-10 h-10 rounded border border-slate-300 cursor-pointer" />
                      <input type="text" v-model="brandingColors.activeButton" class="flex-1 px-2 py-1.5 bg-white border border-slate-200 rounded text-xs font-mono" />
                    </div>
                  </div>

                  <div>
                    <label class="text-[10px] font-bold text-slate-600 mb-1 block">Bouton Hover</label>
                    <div class="flex items-center gap-2">
                      <input type="color" v-model="brandingColors.hoverButton" class="w-10 h-10 rounded border border-slate-300 cursor-pointer" />
                      <input type="text" v-model="brandingColors.hoverButton" class="flex-1 px-2 py-1.5 bg-white border border-slate-200 rounded text-xs font-mono" />
                    </div>
                  </div>

                  <div>
                    <label class="text-[10px] font-bold text-slate-600 mb-1 block">Bordure</label>
                    <div class="flex items-center gap-2">
                      <input type="color" v-model="brandingColors.border" class="w-10 h-10 rounded border border-slate-300 cursor-pointer" />
                      <input type="text" v-model="brandingColors.border" class="flex-1 px-2 py-1.5 bg-white border border-slate-200 rounded text-xs font-mono" />
                    </div>
                  </div>
                </div>

                <!-- Aperçu -->
                <div class="pt-4 border-t border-slate-200">
                  <p class="text-[10px] font-bold text-slate-600 mb-2">APERÇU</p>
                  <div class="w-full h-20 rounded-xl border-2 flex items-center px-4 gap-3" :style="{ backgroundColor: brandingColors.background, borderColor: brandingColors.border }">
                    <div class="w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm" :style="{ backgroundColor: brandingColors.activeButton, color: brandingColors.text }">
                      A
                    </div>
                    <div class="flex-1">
                      <p class="text-xs font-bold" :style="{ color: brandingColors.text }">Nom de l'utilisateur</p>
                      <p class="text-[10px] font-bold uppercase" :style="{ color: brandingColors.textMuted }">Rôle</p>
                    </div>
                    <button class="px-3 py-1.5 rounded-lg text-xs font-bold" :style="{ backgroundColor: brandingColors.hoverButton, color: brandingColors.text }">
                      Bouton
                    </button>
                  </div>
                </div>

                <button @click="saveBranding" class="w-full py-2.5 bg-blue-600 text-white rounded-xl font-bold text-xs hover:bg-blue-700 transition-all">
                  Sauvegarder le Branding
                </button>
              </div>
            </section>

            <!-- Actions de sauvegarde -->
            <div class="pt-4 border-t border-slate-100 space-y-3">
              <button @click="updateClient(selectedClient)" class="w-full py-3 bg-blue-600 text-white rounded-xl font-bold uppercase tracking-wider text-xs shadow-sm hover:bg-blue-700 transition-all">
                Enregistrer la Configuration
              </button>
              
              <div class="grid grid-cols-2 gap-2">
                <button @click="resetTickets(selectedClient.id)" class="py-2.5 bg-white text-amber-600 rounded-xl font-bold text-xs hover:bg-amber-50 border border-amber-200 transition-all">
                  Reset Tickets
                </button>
                <button @click="deleteClient(selectedClient.id)" class="py-2.5 bg-white text-rose-600 rounded-xl font-bold text-xs hover:bg-rose-50 border border-rose-200 transition-all">
                  Supprimer l'agence
                </button>
              </div>
            </div>

          </div>

        </div>
      </div>
    </Transition>

  </div>
</template>

<script>
import { toast } from 'vue3-toastify';
import 'vue3-toastify/dist/index.css';
import TablePagination from '@/components/TablePagination.vue';
import MediaPicker from '@/components/MediaPicker.vue';

export default {
  components: {
    TablePagination,
    MediaPicker
  },
  data() {
    return {
      loading: true,
      showAddModal: false,
      selectedClient: null,
      newClient: { nom: '', email: '', bornesTotal: 1, ecrans: 1 },
      searchQuery: '',
      currentPage: 1,
      itemsPerPage: 15,
      clients: [],
      openedKioskId: null,
      editingDeviceId: null,
      editingDeviceName: '',
      branding: null,
      brandingColors: {
        background: '#0B0F19',
        text: '#E8EBF5',
        textMuted: '#8A90B3',
        activeButton: '#3b82f6',
        hoverButton: '#4f46e5',
        border: '#1F2A4A'
      }
    };
  },
  computed: {
    filteredClients() {
      if (!this.searchQuery) return this.clients;
      const query = this.searchQuery.toLowerCase();
      return this.clients.filter(client =>
        client.nom.toLowerCase().includes(query) ||
        client.siret.toLowerCase().includes(query)
      );
    },
    paginatedClients() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredClients.slice(start, end);
    }
  },
  watch: {
    searchQuery() {
      this.currentPage = 1;
    }
  },
  mounted() {
    this.fetchAgencies();
  },
  methods: {
    showKioskKey(kiosk) {
        this.openedKioskId = this.openedKioskId === kiosk._id ? null : kiosk._id
    },

    async fetchAgencies() {
      this.loading = true;
      try {
        const token = localStorage.getItem("superAdminToken");
        const res = await fetch("https://api.datafuse.fr/api/super-admin/agencies", {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (!res.ok) throw new Error("Erreur API");

        const data = await res.json();
        
        this.clients = data.map(agency => ({
          id: agency._id,
          nom: agency.name,
          siret: agency.email,
          bornesTotal: agency.devices.filter(d => d.type === "KIOSK").length,
          bornesActives: agency.devices.filter(d => d.type === "KIOSK" && d.online).length,
          ecrans: agency.devices.filter(d => d.type === "SCREEN").length,
          devices: agency.devices,
          queues: agency.queues,
          status: agency.status.toLowerCase(),
          statusLabel: agency.status === "ACTIVE" ? "Actif" : "Inactif",
          showLanguageScreen: agency.showLanguageScreen !== undefined ? agency.showLanguageScreen : true,
          showFamilyPackScreen: agency.showFamilyPackScreen || false
        }));

      } catch (err) {
        console.error(err);
        toast.error("Impossible de récupérer les agences");
      } finally {
        this.loading = false;
      }
    },

    toggleStatus(client) {
      client.status = client.status === 'active' ? 'suspended' : 'active';
      client.statusLabel = client.status === 'active' ? 'Actif' : 'Inactif';
    },

    toggleLanguageScreen(client) {
      client.showLanguageScreen = !client.showLanguageScreen;
    },

    toggleFamilyPackScreen(client) {
      client.showFamilyPackScreen = !client.showFamilyPackScreen;
    },

    statusClasses(status) {
      switch(status) {
        case 'active': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
        case 'suspended': return 'bg-rose-50 text-rose-700 border-rose-200';
        default: return 'bg-slate-50 text-slate-600 border-slate-200';
      }
    },

    openConfig(client) {
      this.selectedClient = JSON.parse(JSON.stringify(client));
      this.openedKioskId = null;
      this.loadBranding(client.id);
    },

    async loadBranding(agencyId) {
      try {
        const token = localStorage.getItem("superAdminToken");
        const res = await fetch(`https://api.datafuse.fr/api/super-admin/agency-branding/${agencyId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (res.ok) {
          const data = await res.json();
          this.branding = data;
          if (data.sidebarColors) {
            this.brandingColors = { ...data.sidebarColors };
          } else {
            // Réinitialiser aux valeurs par défaut si pas de couleurs
            this.brandingColors = {
              background: '#0B0F19',
              text: '#E8EBF5',
              textMuted: '#8A90B3',
              activeButton: '#3b82f6',
              hoverButton: '#4f46e5',
              border: '#1F2A4A'
            };
          }
          console.log("✅ [ClientView] Branding chargé:", this.branding);
        }
      } catch (err) {
        console.error("❌ [ClientView] Erreur chargement branding:", err);
      }
    },

    async saveBranding() {
      if (!this.selectedClient) return;

      try {
        const token = localStorage.getItem("superAdminToken");
        const body = {
          logo: this.branding?.logo || null,
          sidebarColors: this.brandingColors
        };

        const res = await fetch(`https://api.datafuse.fr/api/super-admin/agency-branding/${this.selectedClient.id}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(body)
        });

        if (!res.ok) throw new Error();

        toast.success("Branding sauvegardé !");
        this.loadBranding(this.selectedClient.id);

      } catch (err) {
        toast.error("Erreur sauvegarde branding");
      }
    },

    onLogoSelected(media) {
      console.log("📸 [ClientView] Logo sélectionné:", media);

      // Initialiser branding si null
      if (!this.branding) {
        this.branding = {
          agencyId: this.selectedClient.id,
          logo: null,
          sidebarColors: { ...this.brandingColors }
        };
      }

      // Créer un nouvel objet pour forcer la réactivité de Vue
      this.branding = {
        ...this.branding,
        logo: {
          mediaId: media._id,
          url: media.url,
          width: media.metadata?.width || null,
          height: media.metadata?.height || null
        }
      };

      console.log("✅ [ClientView] Branding logo mis à jour:", this.branding.logo);
      toast.success("Logo sélectionné ! N'oubliez pas de sauvegarder.");
    },

    async removeLogo() {
      if (!this.selectedClient) return;

      try {
        const token = localStorage.getItem("superAdminToken");
        const res = await fetch(`https://api.datafuse.fr/api/super-admin/agency-branding/${this.selectedClient.id}/logo`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` }
        });

        if (!res.ok) throw new Error();

        this.branding.logo = null;
        toast.success("Logo supprimé !");

      } catch (err) {
        toast.error("Erreur suppression logo");
      }
    },

    async saveNewClient() {
        if (!this.newClient.nom || !this.newClient.email) {
            toast.warn("Veuillez remplir les champs obligatoires");
            return;
        }
        try {
          const token = localStorage.getItem("superAdminToken");
          const body = {
            name: this.newClient.nom,
            email: this.newClient.email,
            kiosksCount: this.newClient.bornesTotal,
            screensCount: this.newClient.ecrans,
            status: "ACTIVE"
          };

          const res = await fetch("https://api.datafuse.fr/api/super-admin/agency", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify(body)
          });

          if (!res.ok) throw new Error();

          toast.success("Agence créée avec succès !");
          this.showAddModal = false;
          this.newClient = { nom: '', email: '', bornesTotal: 1, ecrans: 1 };
          this.fetchAgencies();

        } catch (err) {
          toast.error("Erreur lors de la création de l'agence");
        }
      },

    async updateClient(client) {
      try {
        const token = localStorage.getItem("superAdminToken");
        const devicesData = client.devices.map(d => ({
          _id: d._id,
          anydeskId: d.remoteAccess?.anydeskId || "",
          name: d.name
        }));

        const body = {
          name: client.nom,
          email: client.siret,
          kiosksCount: client.bornesTotal,
          screensCount: client.ecrans,
          status: client.status.toUpperCase(),
          showLanguageScreen: client.showLanguageScreen,
          showFamilyPackScreen: client.showFamilyPackScreen,
          devices: devicesData
        };

        const res = await fetch(`https://api.datafuse.fr/api/super-admin/agency/${client.id}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(body)
        });

        if (!res.ok) throw new Error();

        toast.success("Configuration mise à jour !");
        this.selectedClient = null;
        this.fetchAgencies();

      } catch (err) {
        toast.error("Échec de la sauvegarde");
      }
    },

    startEditDeviceName(device) {
      this.editingDeviceId = device._id;
      this.editingDeviceName = device.name;
    },

    async saveDeviceName(device) {
      if (!this.editingDeviceName || this.editingDeviceName.trim() === '') {
        toast.warn("Le nom ne peut pas être vide");
        return;
      }

      try {
        const token = localStorage.getItem("superAdminToken");
        const body = {
          devices: [{
            _id: device._id,
            name: this.editingDeviceName.trim(),
            anydeskId: device.remoteAccess?.anydeskId || ""
          }]
        };

        const res = await fetch(`https://api.datafuse.fr/api/super-admin/agency/${this.selectedClient.id}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(body)
        });

        if (!res.ok) throw new Error();

        device.name = this.editingDeviceName.trim();
        toast.success("Nom du device mis à jour !");
        this.editingDeviceId = null;

      } catch (err) {
        toast.error("Erreur lors de la mise à jour");
      }
    },

    async resetTickets(agencyId) {
      if (!confirm("⚠️ ACTION IRRÉVERSIBLE : Supprimer TOUS les tickets de cette agence ?")) return;

      try {
        const token = localStorage.getItem("superAdminToken");
        const res = await fetch(`https://api.datafuse.fr/api/super-admin/agency/${agencyId}/reset-tickets`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          }
        });

        if (!res.ok) throw new Error();
        const data = await res.json();
        toast.success(`♻️ Reset terminé : ${data.deletedTickets} tickets supprimés.`);

      } catch (err) {
        toast.error("Erreur lors du reset des tickets");
      }
    },

    async deleteClient(clientId) {
      if (!confirm("‼️ SUPPRESSION DÉFINITIVE : Cette action est irréversible. Continuer ?")) return;

      try {
        const token = localStorage.getItem("superAdminToken");
        const res = await fetch(`https://api.datafuse.fr/api/super-admin/agency/${clientId}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` }
        });

        if (!res.ok) throw new Error();

        toast.success("Agence supprimée du réseau.");
        this.selectedClient = null;
        this.clients = this.clients.filter(c => c.id !== clientId);

      } catch (err) {
        toast.error("Impossible de supprimer l'agence");
      }
    },

    async copyLink(text) {
      try {
        await navigator.clipboard.writeText(text);
        toast.info("Copié dans le presse-papier !", { autoClose: 1500 });
      } catch (err) {
        toast.error("Erreur de copie");
      }
    }
  }
}
</script>

<style scoped>
.slide-right-enter-active, .slide-right-leave-active {
  transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}
.slide-right-enter-from, .slide-right-leave-to {
  transform: translateX(100%);
}
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}
</style>